import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import { Copy, LoaderCircle } from 'lucide-react'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import * as Query from '../../../api/gql/query'
import { CREATE_PHOTO_SHARE_LINK, PHOTO_SHARE_RECIPIENT_CHECK, SHARE_PHOTOS_INTERNALLY } from '../api'
import { describePhotosError, photosErrorKey } from '../lib/errors'
import type { PhotoUi } from '../lib/permissions'
import { defaultExpiryInput, expiryToIso, maxExpiryInput, minExpiryInput, parseEmailList } from '../lib/share'
import type { PhotoShare, PhotoShareSelection } from '../types'

interface Props {
  projectId: string
  selection: PhotoShareSelection | null
  onOpenChange: (open: boolean) => void
  ui: PhotoUi
}

interface BodyProps {
  projectId: string
  selection: PhotoShareSelection
  onClose: () => void
  ui: PhotoUi
  setBusy: (busy: boolean) => void
  onCreated?: () => void
}

const CONFIRM_INTERNAL = 'Photos_error_share_confirm_internal'
const MUTATION_OPTS = { context: { handlesErrors: true }, refetchQueries: ['photoShares'] }

const InternalTab: React.FC<BodyProps> = ({ projectId, selection, onClose, ui, setBusy }) => {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [picked, setPicked] = useState<string[]>([])
  const [message, setMessage] = useState('')
  const { data: memberData } = useQuery(Query.PROJECT_MEMBERS, { variables: { projectId }, skip: !projectId })
  const { data: checkData, error: checkError } = useQuery<{ photoShareRecipientCheck: { user: { id: string; name: string }; hiddenCount: number }[] }>(PHOTO_SHARE_RECIPIENT_CHECK, {
    variables: { projectId, selection, userIds: picked },
    skip: picked.length === 0,
    fetchPolicy: 'network-only',
    context: { handlesErrors: true },
  })
  const checkFailed = picked.length > 0 && !!checkError
  const [share, { loading }] = useMutation(SHARE_PHOTOS_INTERNALLY, MUTATION_OPTS)

  const members = useMemo(() => {
    const all: { id: string; name: string; email: string }[] = (memberData?.project?.users ?? []).map((u: { user: { id: string; name: string; email: string } }) => u.user)
    const q = search.trim().toLowerCase()
    return all.filter((m) => m.id !== ui.userId && (!q || `${m.name} ${m.email}`.toLowerCase().indexOf(q) >= 0))
  }, [memberData, search, ui.userId])
  const hidden = picked.length > 0 ? (checkData?.photoShareRecipientCheck ?? []).filter((r) => r.hiddenCount > 0 && picked.indexOf(r.user.id) >= 0) : []

  const toggle = (id: string) => setPicked((p) => (p.indexOf(id) >= 0 ? p.filter((x) => x !== id) : p.concat(id)))

  const submit = async () => {
    setBusy(true)
    try {
      const res = await share({ variables: { projectId, selection, userIds: picked, message: message.trim() || null } })
      toastr.success(t('Photos_share_photo_count', { count: res.data.sharePhotosInternally.photoCount }), t('Photos_share_sent', { count: picked.length }))
      setBusy(false)
      onClose()
    } catch (e: any) {
      setBusy(false)
      toastr.error(t('Common_error'), describePhotosError(photosErrorKey(e), t))
    }
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>{t('Photos_share_members')}</Label>
        <Input className="h-8 text-sm" placeholder={t('Photos_share_search_members')} value={search} onChange={(e) => setSearch(e.target.value)} />
        <div className="max-h-64 space-y-1 overflow-y-auto rounded-md border p-2">
          {members.map((m) => (
            <label key={m.id} className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm hover:bg-accent">
              <Checkbox checked={picked.indexOf(m.id) >= 0} onCheckedChange={() => toggle(m.id)} />
              <span className="min-w-0 truncate">{m.name}</span>
              <span className="min-w-0 truncate text-muted-foreground">{m.email}</span>
            </label>
          ))}
        </div>
      </div>
      {checkFailed && <p className="text-sm text-destructive">{describePhotosError(photosErrorKey(checkError), t)}</p>}
      {hidden.map((r) => (
        <Alert key={r.user.id} className="py-2 text-sm">
          {t('Photos_share_hidden_for', { name: r.user.name, count: r.hiddenCount })}
        </Alert>
      ))}
      <div className="space-y-2">
        <Label htmlFor="share-message-internal">{t('Photos_share_message')}</Label>
        <Textarea id="share-message-internal" value={message} maxLength={2000} onChange={(e) => setMessage(e.target.value)} />
      </div>
      <div className="flex items-center justify-end gap-2 border-t pt-4">
        <Button variant="outline" disabled={loading} onClick={onClose}>{t('Common_cancel')}</Button>
        <Button disabled={loading || picked.length === 0 || checkFailed} onClick={submit}>
          {loading && <LoaderCircle className="h-4 w-4 animate-spin" />}
          {t('Photos_share_send')}
        </Button>
      </div>
    </div>
  )
}

const ExternalTab: React.FC<BodyProps> = ({ projectId, selection, onClose, setBusy, onCreated }) => {
  const { t } = useTranslation()
  const [expiry, setExpiry] = useState(() => defaultExpiryInput())
  const [password, setPassword] = useState('')
  const [emailsRaw, setEmailsRaw] = useState('')
  const [message, setMessage] = useState('')
  const [needConfirm, setNeedConfirm] = useState(false)
  const [confirmed, setConfirmed] = useState(false)
  const [created, setCreated] = useState<PhotoShare | null>(null)
  const [create, { loading }] = useMutation(CREATE_PHOTO_SHARE_LINK, MUTATION_OPTS)
  const { emails, invalid } = parseEmailList(emailsRaw)

  const submit = async () => {
    setBusy(true)
    try {
      const res = await create({
        variables: {
          projectId,
          selection,
          expiresAt: expiryToIso(expiry),
          password: password || null,
          emails,
          message: message.trim() || null,
          confirmInternal: confirmed,
        },
      })
      setCreated(res.data.createPhotoShareLink)
      onCreated?.()
    } catch (e: any) {
      const key = photosErrorKey(e)
      if (key === CONFIRM_INTERNAL) setNeedConfirm(true)
      else toastr.error(t('Common_error'), describePhotosError(key, t))
    }
    setBusy(false)
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(created?.url ?? '')
      toastr.success('', t('Photos_share_link_copied'))
    } catch (e) {
      toastr.error(t('Common_error'), t('Common_error'))
    }
  }

  if (created) {
    return (
      <div className="space-y-4">
        <div>
          <p className="text-sm font-medium">{t('Photos_share_link_ready')}</p>
          <p className="text-sm text-muted-foreground">{t('Photos_share_photo_count', { count: created.photoCount })}</p>
        </div>
        <Input readOnly value={created.url ?? ''} onFocus={(e) => e.target.select()} />
        <div className="flex items-center justify-end gap-2 border-t pt-4">
          <Button variant="outline" onClick={copy}>
            <Copy className="h-4 w-4" />
            {t('Photos_share_copy_link')}
          </Button>
          <Button onClick={onClose}>{t('Photos_share_done')}</Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="share-expiry">{t('Photos_share_expires')}</Label>
        <Input id="share-expiry" type="date" value={expiry} min={minExpiryInput()} max={maxExpiryInput()} onChange={(e) => setExpiry(e.target.value)} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="share-password">{t('Photos_share_password')}</Label>
        <Input id="share-password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        {password !== '' && password.trim() === '' && <p className="text-sm text-muted-foreground">{t('Photos_share_password_blank')}</p>}
      </div>
      <div className="space-y-2">
        <Label htmlFor="share-emails">{t('Photos_share_emails')}</Label>
        <Input id="share-emails" value={emailsRaw} onChange={(e) => setEmailsRaw(e.target.value)} />
        {invalid.length > 0 && <p className="text-sm text-destructive">{t('Photos_share_emails_invalid', { list: invalid.join(', ') })}</p>}
      </div>
      <div className="space-y-2">
        <Label htmlFor="share-message-external">{t('Photos_share_message')}</Label>
        <Textarea id="share-message-external" value={message} maxLength={2000} onChange={(e) => setMessage(e.target.value)} />
      </div>
      {needConfirm && (
        <label className="flex cursor-pointer items-start gap-2 text-sm">
          <Checkbox className="mt-0.5" checked={confirmed} onCheckedChange={(v) => setConfirmed(v === true)} />
          <span>{t('Photos_share_confirm_internal')}</span>
        </label>
      )}
      <div className="flex items-center justify-end gap-2 border-t pt-4">
        <Button variant="outline" disabled={loading} onClick={onClose}>{t('Common_cancel')}</Button>
        <Button disabled={loading || !expiry || invalid.length > 0 || (needConfirm && !confirmed)} onClick={submit}>
          {loading && <LoaderCircle className="h-4 w-4 animate-spin" />}
          {t('Photos_share_create_link')}
        </Button>
      </div>
    </div>
  )
}

const SharePhotosDialog: React.FC<Props> = ({ projectId, selection, onOpenChange, ui }) => {
  const { t } = useTranslation()
  const [busy, setBusy] = useState(false)
  // a created link must stay on screen until "Done" or close: switching tabs would unmount it
  const [linkCreated, setLinkCreated] = useState(false)
  const external = ui.canExport && ui.isInternal
  const tabsLocked = busy || linkCreated
  const close = () => {
    setLinkCreated(false)
    onOpenChange(false)
  }

  return (
    <Dialog open={!!selection} onOpenChange={(next) => !busy && (next ? onOpenChange(next) : close())}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('Photos_share_title')}</DialogTitle>
        </DialogHeader>
        {selection && (
          <Tabs defaultValue="internal">
            {external && (
              <TabsList>
                <TabsTrigger value="internal" disabled={tabsLocked}>{t('Photos_share_tab_internal')}</TabsTrigger>
                <TabsTrigger value="external" disabled={tabsLocked}>{t('Photos_share_tab_external')}</TabsTrigger>
              </TabsList>
            )}
            <TabsContent value="internal">
              <InternalTab projectId={projectId} selection={selection} onClose={close} ui={ui} setBusy={setBusy} />
            </TabsContent>
            {external && (
              <TabsContent value="external">
                <ExternalTab projectId={projectId} selection={selection} onClose={close} ui={ui} setBusy={setBusy} onCreated={() => setLinkCreated(true)} />
              </TabsContent>
            )}
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default SharePhotosDialog
